import React from "react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";
import { toReadableText } from "../utils/utils";

export function ConfigurationSection({
  title,
  children,
  initiallyOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  initiallyOpen?: boolean;
}) {
  return (
    <Box
      component="details"
      open={initiallyOpen}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        "& > summary": {
          cursor: "pointer",
          py: 1.5,
          fontWeight: 600,
          fontSize: "0.95rem",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "text.primary",
            outlineOffset: 2,
          },
        },
      }}
    >
      <Box component="summary">{title}</Box>
      <Box sx={{ pb: 2 }}>{children}</Box>
    </Box>
  );
}

export function SourceCard({
  systemId,
  name,
  type,
  status,
  action,
  children,
  groups,
}: {
  systemId?: number;
  name: string;
  type?: string;
  status?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  groups: React.ReactNode;
}) {
  return (
    <Card
      component="section"
      data-source-card
      sx={{
        width: "100%",
        minWidth: 0,
        bgcolor: "common.white",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 2px 8px rgba(23,43,77,0.05)",
        scrollMarginTop: "128px",
        "&[data-jump-highlight]": {
          outline: "3px solid",
          outlineColor: "info.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            variant="h5"
            id={systemId != null ? `system-${systemId}` : undefined}
            tabIndex={-1}
            sx={{
              fontFamily: "inherit",
              fontWeight: 600,
              overflowWrap: "anywhere",
              scrollMarginTop: "128px",
            }}
          >
            {name}
          </Typography>
          {type && !/^\d+$/.test(String(type)) && (
            <Typography variant="body2" color="text.secondary">
              {toReadableText(type)}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          {status && (
            <Chip
              label={toReadableText(status)}
              size="small"
              variant="outlined"
              sx={{ maxWidth: { xs: 110, sm: 180 }, fontSize: "0.75rem" }}
            />
          )}
          {action}
        </Stack>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            p: { xs: 2, md: 3 },
            "& button": { textTransform: "none" },
          }}
        >
          {children}
        </Box>
        <Box
          sx={{
            minWidth: 0,
            p: { xs: 2, md: 3 },
            borderLeft: { md: 1 },
            borderTop: { xs: 1, md: 0 },
            borderColor: "divider",
          }}
        >
          {groups}
        </Box>
      </Box>
    </Card>
  );
}
